<template>
    <v-container fluid>
        <v-row>
            <v-col>
                <v-btn
                    color="indigo"
                    class="white--text"
                    :to="{ name: $t('bank-account.routes.create') }"
                >
                    <v-icon left>mdi-plus</v-icon>
                    {{ $t('bank-account.index.buttons.create') }}
                </v-btn>
            </v-col>
        </v-row>

        <v-row>
            <v-col>
                <sarala-table
                    :model="model"
                    ref="table"
                    :cellPadding="true"
                    :applyFilter="applyFilter"
                    :sort="{ sortDesc: true, prop: 'activeText' }"
                >
                    <template #top-left>
                        <v-row class="px-5 py-3">
                            <v-col>
                                <span class="overline">{{
                                    $t('bank-account.breadcrumbs.index')
                                }}</span
                                ><br />
                                <span class="headline">{{
                                    reseller.trade_name
                                }}</span>
                            </v-col>
                        </v-row>
                        <v-tabs v-model="tabActive" @change="changeTab">
                            <v-tab :href="'#with'">
                                {{ $t('bank-account.index.filters.all') }}
                            </v-tab>

                            <v-tab :href="'#active'">
                                {{ $t('bank-account.index.filters.active') }}
                            </v-tab>

                            <v-tab :href="'#only'">
                                {{ $t('bank-account.index.filters.inactive') }}
                            </v-tab>
                        </v-tabs>
                    </template>

                    <template #holder="{ item }">
                        <span class="caption">
                            {{ $t('bank-account.index.table.name') }}:
                        </span>
                        <span class="body-2 font-weight-medium">
                            {{ item.name }}
                        </span>
                        <br />
                        <span class="caption">
                            {{ $t('bank-account.index.table.document') }}:
                        </span>
                        <span class="body-2">{{ item.document }}</span>
                    </template>

                    <template #account="{ item }">
                        <span class="caption">
                            {{ $t('bank-account.index.table.bank') }}:
                        </span>
                        <span class="body-2 font-weight-medium">
                            {{ item.bankName }}
                        </span>
                        <br />
                        <span class="caption">
                            {{ $t('bank-account.index.table.bank-code') }}:
                        </span>
                        <span class="body-2">{{ item.bankCode }}</span>
                        <br />
                        <span class="caption">
                            {{ $t('bank-account.index.table.agency') }}:
                        </span>
                        <span class="body-2">{{ item.agency }}</span>
                        <br />
                        <span class="caption">
                            {{ $t('bank-account.index.table.account-number') }}:
                        </span>
                        <span class="body-2">{{ item.account_number }}</span>
                        <template v-if="item.pix">
                            <br />
                            <span class="caption">
                                {{ $t('bank-account.index.table.pix') }}:
                            </span>
                            <span class="body-2">{{ item.pix }}</span>
                        </template>
                    </template>

                    <template #action="{ item }">
                        <v-tooltip bottom v-if="item.active">
                            <template v-slot:activator="{ on }">
                                <v-btn
                                    icon
                                    tile
                                    v-on="on"
                                    :to="{
                                        name: $t('bank-account.routes.edit'),
                                        params: { id: item.id },
                                    }"
                                >
                                    <v-icon color="blue-grey" v-on="on"
                                        >mdi-pencil</v-icon
                                    >
                                </v-btn>
                            </template>
                            <span>{{ $t('form.actions.edit') }}</span>
                        </v-tooltip>
                        <v-tooltip bottom v-if="item.active">
                            <template v-slot:activator="{ on }">
                                <v-btn
                                    icon
                                    tile
                                    v-on="on"
                                    @click="deleteItem(item)"
                                >
                                    <v-icon color="red" v-on="on"
                                        >mdi-close</v-icon
                                    >
                                </v-btn>
                            </template>
                            <span>{{ $t('form.actions.delete') }}</span>
                        </v-tooltip>
                        <v-tooltip bottom v-if="!item.active">
                            <template v-slot:activator="{ on }">
                                <v-btn
                                    icon
                                    tile
                                    v-on="on"
                                    @click="restoreItem(item)"
                                >
                                    <v-icon color="success" v-on="on"
                                        >mdi-recycle</v-icon
                                    >
                                </v-btn>
                            </template>
                            <span>{{ $t('form.actions.restore') }}</span>
                        </v-tooltip>
                    </template>
                </sarala-table>
            </v-col>
        </v-row>

        <confirm
            ref="confirmRemove"
            :confirmText="$t('dialogs-confirm.buttons.remove')"
            confirmColor="red darken-1"
        ></confirm>
        <confirm
            ref="confirmRestore"
            :confirmText="$t('dialogs-confirm.buttons.restore')"
            confirmColor="green darken-1"
            width="500px"
        ></confirm>
    </v-container>
</template>

<script>
import SaralaTable from '@/shared/components/SaralaTable'
import Confirm from '@/components/dialogs/Confirm'
import BankAccount from '@/models/BankAccount'
import Reseller from '@/models/Reseller'

export default {
    props: ['reseller_id'],

    components: {
        SaralaTable,
        Confirm,
    },

    computed: {
        model() {
            return new BankAccount()
        },
    },

    data: () => {
        return {
            reseller: {},
            tabActive: 'with',
            amountBankAccounts: 0,
        }
    },

    methods: {
        async deleteItem(item) {
            let confirm = await this.$refs.confirmRemove.open(
                this.$t('bank-account.index.messages.delete'),
                this.$t('bank-account.index.messages.delete-confirmation', {
                    'bank-account': item.account_number + ' - ' + item.bankName,
                }),
            )

            if (!confirm) return

            let clone = item.clone()

            let res = await clone.delete()

            if (!res) {
                this.$bus.$emit(
                    'notify',
                    'error',
                    this.$t('bank-account.index.bus.delete-error', {
                        'bank-account':
                            item.account_number + ' - ' + item.bankName,
                    }),
                )
                return
            }

            this.$refs.table.getItems()

            this.$bus.$emit(
                'notify',
                'success',
                this.$t('bank-account.index.bus.delete-success', {
                    'bank-account': item.account_number + ' - ' + item.bankName,
                }),
            )
        },

        async restoreItem(item) {
            let restoreConfirmation =
                this.amountBankAccounts > 0
                    ? this.$t(
                          'bank-account.index.messages.restore-one-active',
                          {
                              'bank-account':
                                  item.account_number + ' - ' + item.bankName,
                          },
                      )
                    : this.$t(
                          'bank-account.index.messages.restore-confirmation',
                          {
                              'bank-account':
                                  item.account_number + ' - ' + item.bankName,
                          },
                      )

            let confirm = await this.$refs.confirmRestore.open(
                this.$t('bank-account.index.messages.restore'),
                restoreConfirmation,
            )

            if (!confirm) return

            let clone = item.clone()

            let res = await clone.restore()

            if (!res) {
                this.$bus.$emit(
                    'notify',
                    'error',
                    this.$t('bank-account.index.bus.restore-error', {
                        'bank-account':
                            item.account_number + ' - ' + item.bankName,
                    }),
                )
                return
            }

            this.$refs.table.getItems()

            this.$bus.$emit(
                'notify',
                'success',
                this.$t('bank-account.index.bus.restore-success', {
                    'bank-account': item.account_number + ' - ' + item.bankName,
                }),
            )
        },

        applyFilter(model) {
            model
                .where('reseller_id', this.reseller_id)
                .with('bank')
                .where('trashed', this.tabActive)
        },

        async getReseller() {
            let resellerModel = new Reseller()
            this.reseller = await resellerModel.find(this.reseller_id)
        },

        changeTab() {
            this.$refs.table.getItems()
        },

        async getAmountBankAccounts() {
            let url = `${this.model.baseUrl()}/count/${this.model.resourceName()}/${
                this.reseller_id
            }`

            let res = await this.$http.get(url).catch()

            this.amountBankAccounts = res ? res.data : 0
        },
    },

    created() {
        this.getReseller()
        this.getAmountBankAccounts()
    },
}
</script>
